import { countryDialCodes } from "./countryDialCodes";

const NON_DIGIT_REGEX = /\D/g;
const NON_DIGIT_OR_PLUS_REGEX = /[^\d+]/g;
const PHONE_FORMAT_SEPARATOR_REGEX = /[\s().-]/g;

const dialCodeEntries = Object.entries(countryDialCodes).map(([countrySymbol, dialDigits]) => ({
    countrySymbol,
    dialDigits: `${dialDigits}`,
}));

const dialCodeEntriesByDialLengthDesc = [...dialCodeEntries].sort((firstEntry, secondEntry) => (
    secondEntry.dialDigits.length - firstEntry.dialDigits.length
));

const countrySymbolsByDialDigits = dialCodeEntries.reduce((acc, entry) => {
    const currentSymbols = acc[entry.dialDigits] || [];
    currentSymbols.push(entry.countrySymbol);
    acc[entry.dialDigits] = currentSymbols;
    return acc;
}, {});

export const normalizeDialCodeToDigits = (dialCode: string) => {
    const normalizedDialCode = `${dialCode || ""}`.trim();
    if (!normalizedDialCode) return "";

    if (/^\+\d+$/.test(normalizedDialCode)) {
        return normalizedDialCode.slice(1);
    }

    if (/^\d+$/.test(normalizedDialCode)) {
        return normalizedDialCode;
    }

    const mappedDialCode = countryDialCodes[normalizedDialCode.toLowerCase()];
    return mappedDialCode || "";
};

export const normalizeDialCodeSymbol = (countrySymbol: string, fallback: string = "at") => {
    const normalizedCountrySymbol = `${countrySymbol || ""}`.trim().toLowerCase();
    if (!normalizedCountrySymbol) return fallback;

    if (countryDialCodes[normalizedCountrySymbol]) {
        return normalizedCountrySymbol;
    }

    const dialDigits = normalizeDialCodeToDigits(normalizedCountrySymbol);
    if (!dialDigits) return fallback;

    const matchedEntry = Object.entries(countryDialCodes).find(([, value]) => value === dialDigits);
    if (matchedEntry) return matchedEntry[0];

    return fallback;
};

const normalizePhoneRaw = (phoneNumber: string) => {
    const normalizedPhone = `${phoneNumber || ""}`.trim();
    if (!normalizedPhone) return "";

    let compactPhone = normalizedPhone.replace(NON_DIGIT_OR_PLUS_REGEX, "");

    if (compactPhone.startsWith("00")) {
        compactPhone = `+${compactPhone.slice(2)}`;
    }

    if (compactPhone.startsWith("+")) {
        return `+${compactPhone.slice(1).replace(NON_DIGIT_REGEX, "")}`;
    }

    return compactPhone.replace(NON_DIGIT_REGEX, "");
};

export const normalizePhoneLocalNumber = (phoneNumber: string, dialCode: string) => {
    const compactPhone = normalizePhoneRaw(phoneNumber);
    if (!compactPhone) return "";

    const dialDigits = normalizeDialCodeToDigits(dialCode);
    if (!dialDigits) return compactPhone.replace(NON_DIGIT_REGEX, "");

    const phoneDigits = compactPhone.startsWith("+")
        ? compactPhone.slice(1)
        : compactPhone;

    let localDigits = phoneDigits;

    const hasExplicitCountryPrefix =
        compactPhone.startsWith(`+${dialDigits}`) ||
        compactPhone.startsWith(`00${dialDigits}`);

    if (hasExplicitCountryPrefix) {
        localDigits = phoneDigits.slice(dialDigits.length);
    } else if (
        phoneDigits.startsWith(dialDigits) &&
        phoneDigits.length > (dialDigits.length + 6)
    ) {
        // Fallback for users entering country code without '+' / '00' prefix.
        localDigits = phoneDigits.slice(dialDigits.length);
    }

    return localDigits.replace(NON_DIGIT_REGEX, "");
};

export const formatPhoneWithDialCode = (dialCode: string, phoneNumber: string, emptyPlaceholder: string = "- - -") => {
    const localPhoneNumber = normalizePhoneLocalNumber(phoneNumber, dialCode);
    if (!localPhoneNumber) return emptyPlaceholder;

    const dialDigits = normalizeDialCodeToDigits(dialCode);
    if (!dialDigits) return localPhoneNumber;

    return `+${dialDigits} ${localPhoneNumber}`;
};

const compactPhoneForCountryDetection = (phoneNumber: string) => `${phoneNumber || ""}`
    .trim()
    .replace(PHONE_FORMAT_SEPARATOR_REGEX, "");

const resolveCountrySymbolForDialDigits = (dialDigits: string, currentCountrySymbol: string = "") => {
    const matchingCountrySymbols = countrySymbolsByDialDigits[dialDigits] || [];
    if (!matchingCountrySymbols.length) return "";

    const normalizedCurrentCountrySymbol = `${currentCountrySymbol || ""}`.trim().toLowerCase();
    if (
        normalizedCurrentCountrySymbol &&
        matchingCountrySymbols.includes(normalizedCurrentCountrySymbol)
    ) {
        return normalizedCurrentCountrySymbol;
    }

    return matchingCountrySymbols[0];
};

const parseExplicitCountrySymbolFromPhone = (phoneNumber: string, currentCountrySymbol: string = "") => {
    const compactPhone = compactPhoneForCountryDetection(phoneNumber);
    const hasExplicitDialPrefix = compactPhone.startsWith("+") || compactPhone.startsWith("00");
    if (!hasExplicitDialPrefix) return "";

    const phoneWithoutPrefix = compactPhone.startsWith("+")
        ? compactPhone.slice(1)
        : compactPhone.slice(2);
    const phoneDigits = phoneWithoutPrefix.replace(NON_DIGIT_REGEX, "");
    if (!phoneDigits) return "";

    const matchedEntry = dialCodeEntriesByDialLengthDesc.find(({dialDigits}) => (
        phoneDigits.startsWith(dialDigits)
    ));
    if (!matchedEntry) return "";

    return resolveCountrySymbolForDialDigits(matchedEntry.dialDigits, currentCountrySymbol);
};

export const normalizePhoneStateOnBlur = ({
    phoneNumber,
    countrySymbol,
    fallbackCountrySymbol = "at",
}: {
    phoneNumber: string;
    countrySymbol: string;
    fallbackCountrySymbol: string;
}) => {
    const normalizedCountrySymbol = normalizeDialCodeSymbol(countrySymbol, fallbackCountrySymbol);
    const explicitCountrySymbol = parseExplicitCountrySymbolFromPhone(
        phoneNumber,
        normalizedCountrySymbol
    );
    const resolvedCountrySymbol = explicitCountrySymbol || normalizedCountrySymbol;

    return {
        countrySymbol: resolvedCountrySymbol,
        phoneNumber: normalizePhoneLocalNumber(phoneNumber, resolvedCountrySymbol),
    };
};
